import { html, View } from 'rune-ts';
import klass from './ProductFilterModal.module.scss';
import { ProductListPageData } from '../../../pages/ProductList/ProductList.page';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { Modal, ModalClosedEvent } from '../../atoms/Modal/Modal';
import {
  ProductFilterBody,
  ProductFilterChangedEvent,
  ProductFilterConfirmEvent,
} from './views/ProductFilterBody';
import { ProductListTypeS } from '../../../../features/ProductList/outbound/share';
import { ProductFilterOption } from '../../../../features/ProductList/type';
import { WikiRestoreIcon } from '../../atoms/Icon';

export type ProductFilterModalData = Pick<ProductListPageData, 'creator_filters' | 'style_filters'> & {
  is_mobile: boolean;
};

class ResetButton extends ButtonAction {}

class ConfirmButton extends ButtonAction {}

export class ProductFilterModal extends View<ProductFilterModalData> {
  FilterBody: ProductFilterBody;
  ResetButton: ButtonAction;
  ConfirmButton: ButtonAction;
  FilterModal: Modal;

  constructor(data: ProductFilterModalData, private filter: ProductListTypeS.ProductFilterOption) {
    super(data);

    this.FilterBody = new ProductFilterBody(this.data, this.filter);

    this.ResetButton = new ResetButton({
      label: this.data.is_mobile ? html`${WikiRestoreIcon}` : ET('mps2::common::reset'),
      size: 'large',
      klass: klass.reset,
      type: this.FilterBody.isValueSelected() ? 'white' : 'disabled',
    });

    this.ConfirmButton = new ConfirmButton({
      label: ET('mps2::product::filter::confirm'),
      size: 'large',
      klass: klass.confirm,
      type: 'disabled',
    });

    this.FilterModal = new Modal({
      title: ET('mps2::product::filter::title'),
      size: this.data.is_mobile ? 'large' : 'medium',
      isOpen: false,
      backdrop: true,
      body: this.FilterBody,
      footer: html`${this.ResetButton}${this.ConfirmButton}`,
      container_klass: klass.product_filter_modal,
      enable_app_bottom_padding: true,
    });
  }

  override template() {
    return html`<div>${this.FilterModal}</div> `;
  }

  override onRender() {
    this.delegate(ModalClosedEvent<{ is_confirmed: boolean } | undefined>, Modal, (e) => {
      const is_confirmed = e.detail?.is_confirmed;
      if (!is_confirmed) {
        this.undo();
      }
    });

    this.delegate('click', ResetButton, () => {
      const value = this.clear();
      this.dispatchEvent(ProductFilterConfirmEvent, {
        bubbles: true,
        detail: value,
      });
      this.close();
    });

    this.delegate('click', ConfirmButton, () => {
      this.dispatchEvent(ProductFilterConfirmEvent, {
        bubbles: true,
        detail: this.FilterBody.getValue(),
      });
      this.FilterModal.close({ is_confirmed: true });
    });

    this.delegate(ProductFilterChangedEvent, ProductFilterBody, () => {
      this.changeButtonActivation();
    });
  }

  open() {
    this.FilterModal.open();
  }

  close() {
    this.FilterModal.close({ is_confirmed: true });
  }

  clear(): ProductFilterOption {
    this.FilterBody.clear();
    this.FilterBody.redraw();
    this.save();
    this.changeButtonActivation();
    return this.FilterBody.getValue();
  }

  undo() {
    this.updateFilter(this.filter);
  }

  save() {
    this.filter = this.FilterBody.getValue();
  }

  updateFilter(filter: ProductListTypeS.ProductFilterOption) {
    this.FilterBody.setValue(filter);
    this.FilterBody.redraw();
    this.filter = filter;
    this.changeButtonActivation();
  }

  private isChanged() {
    function compareArrays<T = any>(arr1: T[], arr2: T[]): boolean {
      if (arr1.length !== arr2.length) {
        return false;
      }

      for (const element of arr1) {
        if (!arr2.includes(element)) {
          return false;
        }
      }

      for (const element of arr2) {
        if (!arr1.includes(element)) {
          return false;
        }
      }

      return true;
    }

    function compareArraysWithoutDuplicates<T = any>(arr1: T[], arr2: T[]) {
      const unique_arr1 = Array.from(new Set(arr1));
      const unique_arr2 = Array.from(new Set(arr2));

      // 두 배열 비교
      return compareArrays(unique_arr1, unique_arr2);
    }

    const current = this.FilterBody.getValue();
    const not_changed =
      current.min_price === this.filter.min_price &&
      current.max_price === this.filter.max_price &&
      compareArraysWithoutDuplicates(
        current.creator_filter_ids ?? [],
        this.filter.creator_filter_ids ?? [],
      ) &&
      compareArraysWithoutDuplicates(current.style_filter_ids ?? [], this.filter.style_filter_ids ?? []);

    return !not_changed;
  }

  private changeButtonActivation() {
    this._changeResetButtonActivation();
    this._changeConfirmButtonActivation();
  }

  private _changeResetButtonActivation() {
    if (this.FilterBody.isValueSelected()) {
      this.ResetButton.data.type = 'white';
    } else {
      this.ResetButton.data.type = 'disabled';
    }
    this.ResetButton.redraw();
  }

  private _changeConfirmButtonActivation() {
    this.ConfirmButton.data.type = this.isChanged() ? 'blue' : 'disabled';
    this.ConfirmButton.redraw();
  }
}
